const VIDEO_SELECTOR = '.product-media__video';
const OBSERVED = new WeakSet();
/** @type {WeakMap<HTMLVideoElement, boolean>} */
const intersecting = new WeakMap();

/** @type {IntersectionObserver | null} */
let intersectionObserver = null;

/** @type {MediaQueryList | null} */
let motionQuery = null;

const prefersReducedMotion = () => Boolean(motionQuery?.matches);

/**
 * @param {HTMLVideoElement} video
 * @returns {boolean}
 */
const isActiveSlideVideo = (video) => {
  const slide = video.closest('slideshow-slide');

  if (!slide) return true;

  return slide.getAttribute('aria-hidden') === 'false';
};

/**
 * @param {HTMLVideoElement} video
 */
const pauseCardVideo = (video) => {
  video.pause();
};

/**
 * @param {HTMLVideoElement} video
 */
const playCardVideo = (video) => {
  if (prefersReducedMotion()) return;

  const playPromise = video.play();

  if (playPromise !== undefined) {
    playPromise.catch(() => {});
  }
};

/**
 * @param {HTMLVideoElement} video
 */
const syncVideoPlayback = (video) => {
  if (
    intersecting.get(video) &&
    isActiveSlideVideo(video) &&
    !prefersReducedMotion()
  ) {
    playCardVideo(video);
  } else {
    pauseCardVideo(video);
  }
};

const getIntersectionObserver = () => {
  if (intersectionObserver) return intersectionObserver;

  intersectionObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const video = entry.target;

        if (!(video instanceof HTMLVideoElement)) continue;

        intersecting.set(video, entry.isIntersecting);
        syncVideoPlayback(video);
      }
    },
    { rootMargin: '50px 0px', threshold: 0.25 }
  );

  return intersectionObserver;
};

/**
 * @param {ParentNode} [root]
 */
const observeCardVideos = (root = document) => {
  const videos = root.querySelectorAll(VIDEO_SELECTOR);

  for (const node of videos) {
    if (!(node instanceof HTMLVideoElement)) continue;
    if (OBSERVED.has(node)) continue;

    OBSERVED.add(node);
    node.muted = true;
    node.playsInline = true;
    node.setAttribute('aria-hidden', 'true');

    if (prefersReducedMotion()) {
      pauseCardVideo(node);
      continue;
    }

    getIntersectionObserver().observe(node);
  }
};

/**
 * @param {Element} slide
 */
const syncVideosInSlide = (slide) => {
  const videos = slide.querySelectorAll(VIDEO_SELECTOR);

  for (const node of videos) {
    if (!(node instanceof HTMLVideoElement)) continue;
    syncVideoPlayback(node);
  }
};

const pauseAllCardVideos = () => {
  const videos = document.querySelectorAll(VIDEO_SELECTOR);

  for (const node of videos) {
    if (!(node instanceof HTMLVideoElement)) continue;
    pauseCardVideo(node);
  }
};

const syncMotionPreference = () => {
  if (prefersReducedMotion()) {
    pauseAllCardVideos();

    if (intersectionObserver) {
      intersectionObserver.disconnect();
      intersectionObserver = null;
    }

    return;
  }

  const videos = document.querySelectorAll(VIDEO_SELECTOR);

  for (const node of videos) {
    if (!(node instanceof HTMLVideoElement)) continue;
    getIntersectionObserver().observe(node);
  }
};

const initCardProductVideo = () => {
  motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  motionQuery.addEventListener('change', syncMotionPreference);

  observeCardVideos();

  const mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes') {
        const target = mutation.target;

        if (
          target instanceof Element &&
          target.matches('slideshow-slide')
        ) {
          syncVideosInSlide(target);
        }

        continue;
      }

      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;

        if (node.matches?.(VIDEO_SELECTOR)) {
          observeCardVideos(node.parentElement ?? document);
        } else if (node.querySelector?.(VIDEO_SELECTOR)) {
          observeCardVideos(node);
        }
      }
    }
  });

  mutationObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-hidden'],
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCardProductVideo, {
    once: true,
  });
} else {
  initCardProductVideo();
}
